import { sheets } from '@/app/grid/controller/Sheets';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { focusGrid } from '@/app/helpers/focusGrid';
import { captureException } from '@sentry/react';
import { Matrix, Rectangle, Renderer } from 'pixi.js';

const resolution = 4;
const borderSize = 1;
const maxTextureSize = 4096;

let renderer: Renderer | undefined;

export const getScreenImage = async (): Promise<Blob | null> => {
  return new Promise((resolve) => {
    pixiApp.renderer.view.toBlob?.((blob) => resolve(blob));
  });
};

/** Throws away the cached renderer so the next export builds a fresh context. */
const disposeRenderer = () => {
  try {
    renderer?.destroy(false);
  } catch (error) {
    // Tearing down a dead context can itself throw; there's nothing to do
    // about it beyond dropping our reference below.
    console.warn('[copyAsPNG] failed to destroy the offscreen renderer.', error);
  }
  renderer = undefined;
};

/** Returns the offscreen renderer, or undefined when WebGL is unavailable. */
const getRenderer = (): Renderer | undefined => {
  // The cached renderer's WebGL context can die between exports (GPU reset, or
  // the browser evicting one of the page's contexts). Rendering into a lost
  // context throws deep inside pixi's uniform sync, so replace it instead.
  if (renderer && renderer.context.isLost) {
    disposeRenderer();
  }
  if (!renderer) {
    try {
      renderer = new Renderer({
        resolution,
        antialias: true,
        backgroundColor: 0xffffff,
      });
    } catch (error) {
      // Creating a second WebGL context can fail (no WebGL support, hit the
      // browser's per-page context limit, etc.). Downgrade to a warning —
      // the caller shows a user-facing snackbar when we return null.
      console.warn('[copyAsPNG] WebGL renderer unavailable; copy-as-PNG disabled.', error);
      captureException(error, {
        level: 'warning',
        extra: { context: 'copyAsPNG renderer init (WebGL unavailable)' },
      });
      return undefined;
    }
  }
  return renderer;
};

/** returns a dataURL to a copy of the selected cells */
export const copyAsPNG = async (): Promise<Blob | null> => {
  const activeRenderer = getRenderer();
  if (!activeRenderer) return null;

  // A selected native chart exports just that chart (no gridlines); otherwise
  // the current cell selection is exported. This mirrors the copy/cut payload
  // resolution in clipboard.tsx.
  const chart = content.chartsLayer.selected;
  const sheetId = sheets.current;
  const gridLines = !chart;

  let screenRect: Rectangle;
  if (chart) {
    // the chart's border stroke is centered on its bounds, so half of it
    // falls outside chart.w/h; grow the capture so the bottom-right border
    // isn't clipped (the borderSize / 2 translate below covers the top-left)
    screenRect = new Rectangle(chart.x, chart.y, chart.w + borderSize, chart.h + borderSize);
  } else {
    const rect = sheets.sheet.cursor.getLargestRectangle();
    if (!rect) return null;
    screenRect = sheets.sheet.getScreenRectangle(rect.x, rect.y, rect.width, rect.height);

    // captures bottom-right border size
    screenRect.width += borderSize * 2 - 1;
    screenRect.height += borderSize * 2 - 1;
  }

  let imageWidth = screenRect.width * resolution,
    imageHeight = screenRect.height * resolution;
  if (Math.max(imageWidth, imageHeight) > maxTextureSize) {
    if (imageWidth > imageHeight) {
      imageHeight = imageHeight * (maxTextureSize / imageWidth);
      imageWidth = maxTextureSize;
    } else {
      imageWidth = imageWidth * (maxTextureSize / imageHeight);
      imageHeight = maxTextureSize;
    }
  }
  activeRenderer.resize(imageWidth, imageHeight);
  activeRenderer.view.width = imageWidth;
  activeRenderer.view.height = imageHeight;
  await content.prepareForCopying({ sheetId, cull: screenRect, gridLines });

  try {
    // Update gridlines for the specific rectangle before rendering
    if (gridLines) {
      const gridLinesScale = pixiApp.viewport.scale.x * resolution;
      content.gridLines.update(screenRect, gridLinesScale, true);
    }

    const transform = new Matrix();
    transform.translate(-screenRect.x + borderSize / 2, -screenRect.y + borderSize / 2);
    const scale = imageWidth / (screenRect.width * resolution);
    transform.scale(scale, scale);
    activeRenderer.render(content, { transform });
  } catch (error) {
    // The render can throw from inside pixi (e.g. the offscreen context died
    // after we checked it). Drop the renderer so the next export starts from a
    // fresh context, and return null so the caller shows its error snackbar.
    console.warn('[copyAsPNG] render failed; discarding the offscreen renderer.', error);
    captureException(error, { level: 'warning', extra: { context: 'copyAsPNG render' } });
    disposeRenderer();
    return null;
  } finally {
    // prepareForCopying sets content.copying, which halts the on-screen render
    // loop — it must be cleared even when the render above throws, or the grid
    // stays frozen until reload.
    const viewportBounds = pixiApp.viewport.getVisibleBounds();
    content.cleanUpAfterCopying(viewportBounds);
    // Restore gridlines for the viewport
    content.gridLines.update(undefined, undefined, true);

    // force a pixiApp rerender to clean up interactions (I think)
    pixiApp.setViewportDirty();
  }

  focusGrid();
  return new Promise((resolve) => {
    activeRenderer.view.toBlob?.((blob) => resolve(blob));
  });
};
